import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../../DesignSystem/Buttons/SecondaryButton";
import OutlinedStatusButton from "../../../../DesignSystem/Buttons/OutlinedStatusButtons";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import FileUpload from "../../../../DesignSystem/Upload/FileUpload";
import NewTextArea from "../../../../DesignSystem/Inputs/NewTextArea";
import CheckboxGroup from "../../../../DesignSystem/Inputs/CheckboxGroup";
import Tabs from "../../../../DesignSystem/Tabs/Tabs";
import SummaryCards from "../../../../DesignSystem/SummaryCards/SummaryCards";
import CustomSelect from "../../../../DesignSystem/Dropdowns/CustomSelect";
import IconButton from "../../../../DesignSystem/Buttons/IconButton";
import { PostCard } from "../../../Tabs/HelperFunctions/HelperFunctions";
import { ReactComponent as EditIcon } from "../../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as CloseIcon } from "../../../../../assets/icons/paraplanningDashboard/Cross.svg";
import { useAdviserList } from "../../../../../context/Adviser/Utils/AdviserEmailList";
import LicenseCertificateEditLogs from "./LicenseCertificateEditLogs";
import { useCanEditLicenceTabs } from "../utils/accessPermissions";
import {
  commitCpdReportPreview,
  formatCpdHours,
  previewCpdReportFiles,
} from "../utils/cpdRecords";
import { formatDateOnly } from "../utils/licenseCertificateTables";
import "../../../../../css/Adviser/EditAdviserDetailsPage.css";
import "../../../../../css/Adviser/LicenseCertificatesSummary.css";

const getCpdRowKey = (row) =>
  `${row.reportType}:${row.sourceFileName}:${row.sourceRowNumber}`;

const isResolvedCpdRow = (row) =>
  row.status === "Matched" || row.status === "Fixed";

const CpdReviewTable = ({
  reportType,
  rows,
  adviserOptions,
  onEdit,
  onMapAdviser,
  onUnmapAdviser,
}) => {
  const reportLabel = reportType === "idd" ? "IDD" : "All CPD";
  const [mappingRow, setMappingRow] = useState(null);
  const [mappingPosition, setMappingPosition] = useState(null);

  const openAdviserMapping = (event, row) => {
    event.stopPropagation();

    const anchor = event.currentTarget.getBoundingClientRect();
    const popupWidth = 360;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    setMappingPosition({
      left: Math.max(
        12,
        Math.min(anchor.left - popupWidth - 8, viewportWidth - popupWidth - 12),
      ),
      top: Math.max(12, Math.min(anchor.top, viewportHeight - 360)),
    });
    setMappingRow(row);
  };

  const closeAdviserMapping = () => {
    setMappingRow(null);
    setMappingPosition(null);
  };

  return (
    <PostCard title={`${reportLabel} report review`}>
      <div className="lcTableWrap">
        <table className="lcTable">
          <thead>
            <tr>
              <th className="lcIndexColumn lcPinnedColumn" aria-label="Row" />
              <th className="lcPinnedColumn lcPinnedColumnLeft">
                <span className="FP_Help_Text_Semibold">Adviser Name</span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">Uploaded Email</span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">Report Date</span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">
                  {reportType === "idd"
                    ? "Total IDD Hours"
                    : "Total CPD Hours"}
                </span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">Structured Hours</span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">
                  Non-Structured Hours
                </span>
              </th>
              <th>
                <span className="FP_Help_Text_Semibold">Details</span>
              </th>
              <th className="lcPinnedColumn lcPinnedColumnRight">
                <span className="FP_Help_Text_Semibold">Status</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.slice(0, 300).map((row, index) => {
                const matched = isResolvedCpdRow(row);
                const fixed = row.status === "Fixed";
                const name =
                  [row.firstName, row.lastName].filter(Boolean).join(" ") ||
                  "-";

                return (
                  <tr
                    key={getCpdRowKey(row)}
                    onClick={() => matched && onEdit(row)}
                    style={{ cursor: matched ? "pointer" : "default" }}
                    title={
                      matched
                        ? "Open this adviser's Licence Certificates edit page"
                        : row.reason
                    }
                  >
                    <td className="lcIndexColumn lcPinnedColumn">
                      <span className="FP_Help_Text">{index + 1}.</span>
                    </td>
                    <td className="lcPinnedColumn lcPinnedColumnLeft">
                      <span className="FP_Help_Text primary-text">{name}</span>
                    </td>
                    <td>
                      <div className="lcStatusCell">
                        {row.status !== "Matched" ? (
                          <IconButton
                            icon={<EditIcon aria-hidden="true" />}
                            label="Map row to an adviser"
                            onClick={(event) => openAdviserMapping(event, row)}
                          />
                        ) : null}
                        <span className="FP_Help_Text">
                          {row.uploadedEmail || row.email || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="FP_Help_Text">
                        {formatDateOnly(row.reportDate)}
                      </span>
                    </td>
                    <td>
                      <span className="FP_Help_Text">
                        {formatCpdHours(row.cpdMinutes)}
                      </span>
                    </td>
                    <td>
                      <span className="FP_Help_Text">
                        {formatCpdHours(row.structuredMinutes)}
                      </span>
                    </td>
                    <td>
                      <span className="FP_Help_Text">
                        {formatCpdHours(row.nonStructuredMinutes)}
                      </span>
                    </td>
                    <td>
                      {matched ? (
                        <span className="FP_Help_Text primary-text">
                          {fixed
                            ? `Mapped to ${row.storedEmail}`
                            : "Edit adviser"}
                        </span>
                      ) : (
                        <span className="FP_Help_Text">{row.reason}</span>
                      )}
                    </td>
                    <td className="lcPinnedColumn lcPinnedColumnRight">
                      <div className="lcStatusCell">
                        <OutlinedStatusButton
                          label={row.status}
                          color={fixed ? "blue" : matched ? "green" : "red"}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="lcEmptyCell FP_Help_Text"
                >
                  No rows were found in this report.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {mappingRow && mappingPosition
        ? createPortal(
          <div
            className="lcCpdMappingPopover"
            style={mappingPosition}
            role="dialog"
            aria-label="Map uploaded CPD record to an adviser"
            onClick={(event) => event.stopPropagation()}
          >
            <IconButton
              className="lcCpdMappingClose"
              icon={<CloseIcon aria-hidden="true" />}
              label="Close adviser mapping"
              onClick={closeAdviserMapping}
            />
            <CustomSelect
              label={`Map ${mappingRow.uploadedEmail || "this row"} to adviser`}
              options={adviserOptions}
              value=""
              placeholder="Select adviser"
              required
              onChange={(event) => {
                if (onMapAdviser(mappingRow, event.target.value) !== false) {
                  closeAdviserMapping();
                }
              }}
            />
            {mappingRow.status === "Fixed" ? (
              <div className="smEditActionsRight">
                <SecondaryButton
                  label="Unmap adviser"
                  onClick={() => {
                    onUnmapAdviser(mappingRow);
                    closeAdviserMapping();
                  }}
                />
              </div>
            ) : null}
          </div>,
          document.body,
        )
        : null}
      {rows.length > 300 ? (
        <p className="Content-Small muted">
          Showing the first 300 rows. All matched rows will still be added.
        </p>
      ) : null}
    </PostCard>
  );
};

export default function CpdRecordsPage() {
  const navigate = useNavigate();
  const canEdit = useCanEditLicenceTabs();
  const {
    advisers: appointedAdvisers,
    loadingAdvisers,
  } = useAdviserList();
  const [allFile, setAllFile] = useState(null);
  const [iddFile, setIddFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [activeReviewTab, setActiveReviewTab] = useState("all");
  const [confirmedForImport, setConfirmedForImport] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const adviserOptions = useMemo(
    () =>
      appointedAdvisers
        .map((adviser) => {
          const email = String(adviser.email || adviser.id || "")
            .trim()
            .toLowerCase();
          const adviserId = adviser.adviserId || adviser.id;
          const displayName =
            adviser.name ||
            [adviser.firstName, adviser.lastName].filter(Boolean).join(" ") ||
            email;

          return {
            id: adviserId,
            name: email ? `${displayName} (${email})` : displayName,
            email,
            firstName: adviser.firstName || adviser.preferredName || "",
            lastName: adviser.lastName || "",
          };
        })
        .filter((adviser) => adviser.id && adviser.email)
        .sort((left, right) => left.name.localeCompare(right.name, "en-GB")),
    [appointedAdvisers],
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleAnalyse = async () => {
    try {
      setAnalysing(true);
      setConfirmedForImport(false);
      clearMessages();
      const nextPreview = await previewCpdReportFiles({
        allFile,
        iddFile,
        advisers: appointedAdvisers,
      });
      setPreview(nextPreview);
      setConfirmedForImport(false);
      setActiveReviewTab(
        nextPreview.sources.some((source) => source.reportType === "all")
          ? "all"
          : "idd",
      );
    } catch (analyseError) {
      setPreview(null);
      setError(analyseError.message || "The CPD reports could not be analysed.");
    } finally {
      setAnalysing(false);
    }
  };

  const handleImport = async () => {
    try {
      setSaving(true);
      clearMessages();
      if (!confirmedForImport) {
        throw new Error(
          "Confirm that the reviewed details are correct before updating the matched records.",
        );
      }
      if (!hasBothReports) {
        throw new Error(
          "Both the All CPD report and the IDD report must be uploaded before adding matched records.",
        );
      }
      const result = await commitCpdReportPreview(preview, {
        allFile,
        iddFile,
      });
      setSuccess(
        `${result.written} CPD record${result.written === 1 ? "" : "s"} added. ${result.unmatched} unmatched row${result.unmatched === 1 ? "" : "s"} were not added.`,
      );
      setPreview(null);
      setAllFile(null);
      setIddFile(null);
      setConfirmedForImport(false);
    } catch (saveError) {
      setError(saveError.message || "The CPD records could not be added.");
    } finally {
      setSaving(false);
    }
  };

  const reviewRows = useMemo(() => {
    if (!preview?.rows) return [];
    const statusOrder = {
      Unmatched: 0,
      Duplicate: 0,
      Fixed: 1,
      Matched: 2,
    };

    return [...preview.rows].sort((a, b) => {
      const statusDifference =
        (statusOrder[a.status] ?? 0) - (statusOrder[b.status] ?? 0);
      if (statusDifference) return statusDifference;
      return Number(a.sourceRowNumber || 0) - Number(b.sourceRowNumber || 0);
    });
  }, [preview]);

  const reviewRowsByType = useMemo(
    () => ({
      all: reviewRows.filter((row) => row.reportType === "all"),
      idd: reviewRows.filter((row) => row.reportType === "idd"),
    }),
    [reviewRows],
  );

  const hasBothReports = useMemo(() => {
    const reportTypes = new Set(
      (preview?.sources || []).map((source) => source.reportType),
    );
    return reportTypes.has("all") && reportTypes.has("idd");
  }, [preview]);

  const handleEditAdviser = (row) => {
    const adviserKey = row.adviserId || row.storedEmail || row.email;
    if (!adviserKey) return;

    navigate(
      `/advisers/${encodeURIComponent(adviserKey)}/license-certificates/edit`,
      {
        state: {
          adviserKey,
          adviserId: row.adviserId || "",
          adviserEmail: row.storedEmail || row.email,
          scrollToSection: "cpd",
          returnTo: "/compliance/license-certificates/cpd-records",
        },
      },
    );
  };

  const handleMapAdviser = (row, adviserId) => {
    const adviser = adviserOptions.find(
      (option) => option.id === adviserId,
    );
    if (!adviser) return false;

    const selectedRowKey = getCpdRowKey(row);
    const rowsToMap = (preview?.rows || []).filter(
      (candidate) => getCpdRowKey(candidate) === selectedRowKey,
    );
    const rowKeysToMap = new Set(rowsToMap.map(getCpdRowKey));
    const hasConflict = rowsToMap.some((targetRow) =>
      (preview?.rows || []).some(
        (candidate) =>
          !rowKeysToMap.has(getCpdRowKey(candidate)) &&
          candidate.reportType === targetRow.reportType &&
          isResolvedCpdRow(candidate) &&
          candidate.adviserId === adviser.id,
      ),
    );

    if (hasConflict) {
      setError(
        "That adviser is already matched to another row in this report.",
      );
      setSuccess("");
      return false;
    }

    const nextRows = (preview?.rows || []).map((candidate) =>
      rowKeysToMap.has(getCpdRowKey(candidate))
        ? {
            ...candidate,
            adviserId: adviser.id,
            storedEmail: adviser.email,
            email: adviser.email,
            firstName: adviser.firstName,
            lastName: adviser.lastName,
            mappingSource: candidate.mappingSource || {
              status: candidate.status,
              reason: candidate.reason,
              email: candidate.uploadedEmail || candidate.email,
              firstName: candidate.firstName,
              lastName: candidate.lastName,
            },
            status: "Fixed",
            reason: "Manually mapped to selected adviser.",
          }
        : candidate,
    );

    setPreview((current) => ({
      ...current,
      rows: nextRows,
      matchedRows: nextRows.filter(isResolvedCpdRow),
      unmatchedRows: nextRows.filter(
        (candidate) => !isResolvedCpdRow(candidate),
      ),
    }));
    setConfirmedForImport(false);
    setError("");
    setSuccess(
      `${rowsToMap.length} row${rowsToMap.length === 1 ? "" : "s"} mapped to ${adviser.email}.`,
    );
    return true;
  };

  const handleUnmapAdviser = (row) => {
    const selectedRowKey = getCpdRowKey(row);
    const nextRows = (preview?.rows || []).map((candidate) => {
      if (getCpdRowKey(candidate) !== selectedRowKey) return candidate;

      const source = candidate.mappingSource || {};
      return {
        ...candidate,
        adviserId: "",
        storedEmail: "",
        email: source.email || candidate.uploadedEmail || "",
        firstName: source.firstName || "",
        lastName: source.lastName || "",
        status: source.status || "Unmatched",
        reason: source.reason || "Manual adviser mapping removed.",
        mappingSource: undefined,
      };
    });

    setPreview((current) => ({
      ...current,
      rows: nextRows,
      matchedRows: nextRows.filter(isResolvedCpdRow),
      unmatchedRows: nextRows.filter(
        (candidate) => !isResolvedCpdRow(candidate),
      ),
    }));
    setConfirmedForImport(false);
    setError("");
    setSuccess("The adviser mapping was removed.");
  };

  const insights = useMemo(() => {
    const average = (rows, field) => {
      if (!rows.length) return "0";
      const total = rows.reduce(
        (sum, row) => sum + Number(row[field] || 0),
        0,
      );
      return formatCpdHours(total / rows.length);
    };

    const buildInsights = (reportType) => {
      const rows = (preview?.rows || []).filter(
        (row) => row.reportType === reportType,
      );
      const matchedRows = rows.filter(isResolvedCpdRow);

      return {
        uploaded: rows.length,
        matched: matchedRows.length,
        unmatched: rows.length - matchedRows.length,
        averageHours: average(matchedRows, "cpdMinutes"),
      };
    };

    return {
      all: buildInsights("all"),
      idd: buildInsights("idd"),
    };
  }, [preview]);

  const reviewTabs = useMemo(
    () => [
      {
        id: "all",
        label: "All CPD",
        count: reviewRowsByType.all.length,
      },
      {
        id: "idd",
        label: "IDD",
        count: reviewRowsByType.idd.length,
      },
    ],
    [reviewRowsByType],
  );

  const unmatchedEmailText = useMemo(() => {
    const emails = (preview?.unmatchedRows || [])
      .map((row) => String(row.email || "").trim().toLowerCase())
      .filter(Boolean);

    return [...new Set(emails)].sort().join("\n");
  }, [preview]);

  const handleCopyUnmatchedEmails = async () => {
    try {
      await navigator.clipboard.writeText(unmatchedEmailText);
      const count = unmatchedEmailText.split("\n").filter(Boolean).length;
      setSuccess(
        `${count} unmatched email${count === 1 ? "" : "s"} copied.`,
      );
      setError("");
    } catch {
      setError("The unmatched email list could not be copied.");
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>CPD Records</title>
      </Helmet>
      <Sidebar isSetExpanded/>

      <div className="Main-Container">
        <div className="smFeed">
          <Breadcrumb
            items={[
              { label: "Compliance", to: "/Department/Compliance" },
              {
                label: "Licence Certificates",
                to: "/advisers/licence-certificates/summary",
              },
              { label: "CPD Records" },
            ]}
          />

          <div className="smEditActions">
            <div>
              <h3 className="FP_H3">CPD Records</h3>
              <p className="Content-Small muted">
                Upload the All CPD and IDD reports together and review every
                adviser match before saving.
              </p>
            </div>
          </div>

          {!canEdit ? (
            <AlertBanner
              type="error"
              message="You need Licence Certificates Admin or Administrator permission to add CPD records."
              showClose={false}
            />
          ) : (
            <>
              {error ? (
                <AlertBanner
                  type="error"
                  message={error}
                  onClose={() => setError("")}
                />
              ) : null}
              {success ? (
                <AlertBanner
                  type="success"
                  message={success}
                  onClose={() => setSuccess("")}
                />
              ) : null}

              <PostCard title="Upload CPD reports">
                {/* <p className="Content-Small muted">
                  Rows are matched to appointed advisers using the User Email
                  Address column. Unmatched and duplicate rows are shown before
                  anything is saved.
                </p> */}
                <div className="smGrid2">
                  <FileUpload
                    label="All CPD report"
                    isRequired
                    description="Drop the All CPD Excel or CSV file here or"
                    accept=".csv,.xlsx,.xls,text/csv"
                    fileName={allFile?.name || ""}
                    status={allFile ? "success" : "default"}
                    statusMessage={
                      allFile ? `Selected: ${allFile.name}` : undefined
                    }
                    onFilesChange={(file) => {
                      setAllFile(file);
                      setPreview(null);
                      setConfirmedForImport(false);
                      clearMessages();
                    }}
                  />
                  <FileUpload
                    label="IDD report"
                    isRequired
                    description="Drop the IDD Excel or CSV file here or"
                    accept=".csv,.xlsx,.xls,text/csv"
                    fileName={iddFile?.name || ""}
                    status={iddFile ? "success" : "default"}
                    statusMessage={
                      iddFile ? `Selected: ${iddFile.name}` : undefined
                    }
                    onFilesChange={(file) => {
                      setIddFile(file);
                      setPreview(null);
                      setConfirmedForImport(false);
                      clearMessages();
                    }}
                  />
                </div>

                <div className="smEditActionsRight">
                  <PrimaryButton
                    label={
                      loadingAdvisers
                        ? "Loading advisers..."
                        : analysing
                          ? "Checking..."
                          : "Review Files"
                    }
                    onClick={handleAnalyse}
                    disabled={
                      loadingAdvisers ||
                      analysing ||
                      saving ||
                      !allFile ||
                      !iddFile
                    }
                  />
                </div>
              </PostCard>

              {preview ? (
                <>
                  <section className="smFeed">
                    <h3 className="FP_Subtitle_Semibold">
                      Upload review summary
                    </h3>
                    <SummaryCards
                      grid="4"
                      cards={[
                        {
                          id: "uploaded-rows",
                          label: "Uploaded rows",
                          value: preview.rows.length,
                          tone: "blue",
                        },
                        {
                          id: "matched-rows",
                          label: "Matched",
                          value: preview.matchedRows.length,
                          tone: "green",
                        },
                        {
                          id: "unmatched-rows",
                          label: "Unmatched or duplicate",
                          value: preview.unmatchedRows.length,
                          tone: "cream",
                        },
                        {
                          id: "report-count",
                          label: "Reports",
                          value: preview.sources.length,
                          tone: "purple",
                        },
                      ]}
                    />

                    <CheckboxGroup
                      options={[
                        {
                          label:
                            "I confirm the details are correct. Proceed with updating the matched records.",
                          value: "confirmed",
                        },
                      ]}
                      selectedOptions={
                        confirmedForImport ? ["confirmed"] : []
                      }
                      formDataKey="confirmCpdMatchedRecords"
                      onChange={(selectedOptions) =>
                        setConfirmedForImport(
                          selectedOptions.includes("confirmed"),
                        )
                      }
                      columns={1}
                      disabled={
                        analysing ||
                        saving ||
                        !hasBothReports ||
                        !preview.matchedRows.length
                      }
                    />

                    <div className="smEditActionsRight">
                      <SecondaryButton
                        label="Clear"
                        onClick={() => {
                          setPreview(null);
                          setAllFile(null);
                          setIddFile(null);
                          setConfirmedForImport(false);
                          clearMessages();
                        }}
                        disabled={saving}
                      />
                      <PrimaryButton
                        label={saving ? "Adding..." : "Add Matched Records"}
                        onClick={handleImport}
                        disabled={
                          saving ||
                          analysing ||
                          !hasBothReports ||
                          !preview.matchedRows.length ||
                          !confirmedForImport
                        }
                      />
                    </div>
                  </section>

                  {/* {unmatchedEmailText ? (
                    <PostCard title="Unmatched emails from both reports">
                      <NewTextArea
                        id="cpd-unmatched-emails"
                        label=""
                        value={unmatchedEmailText}
                        readOnly
                        maxLength={20000}
                        helperText=""
                      />
                      <div className="smEditActionsRight">
                        <SecondaryButton
                          label="Copy Unmatched Emails"
                          onClick={handleCopyUnmatchedEmails}
                        />
                      </div>
                    </PostCard>
                  ) : null} */}

                  <Tabs
                    tabs={reviewTabs}
                    activeTab={activeReviewTab}
                    onTabChange={setActiveReviewTab}
                    noShadow
                  />

                  <PostCard
                    title={
                      activeReviewTab === "idd"
                        ? "IDD insights"
                        : "All CPD insights"
                    }
                  >
                    <div className="smGrid2">
                      <div className="Content-Small">
                        Uploaded rows: {insights[activeReviewTab].uploaded}
                      </div>
                      <div className="Content-Small">
                        Advisers matched: {insights[activeReviewTab].matched}
                      </div>
                      <div className="Content-Small">
                        Unmatched or duplicate:{" "}
                        {insights[activeReviewTab].unmatched}
                      </div>
                      <div className="Content-Small">
                        {activeReviewTab === "idd"
                          ? "Average IDD hours"
                          : "Average total CPD hours"}
                        : {insights[activeReviewTab].averageHours}
                      </div>
                    </div>
                  </PostCard>

                  <CpdReviewTable
                    key={activeReviewTab}
                    reportType={activeReviewTab}
                    rows={reviewRowsByType[activeReviewTab]}
                    adviserOptions={adviserOptions}
                    onEdit={handleEditAdviser}
                    onMapAdviser={handleMapAdviser}
                    onUnmapAdviser={handleUnmapAdviser}
                  />
                </>
              ) : null}

              <LicenseCertificateEditLogs variant="cpdReports" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
